import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import PasswordInput from '@components/forms/PasswordInput';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Label } from '@components/ui/Label';
import { useAuthStore } from '@store/authStore';
import { RiGoogleLine, RiMicrosoftLine } from "@remixicon/react";
import { toast } from 'sonner';
import { encodeData } from '@/utils';

// SignUpForm component for reuse
export function SignUpForm() {
  const navigate = useNavigate();
  const { setAuthType } = useAuthStore();
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [brokerageEmail, setBrokerageEmail] = React.useState("");
  const [acceptTerms, setAcceptTerms] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [userTypes, setUserTypes] = React.useState([]);
  const [currentStep, setCurrentStep] = React.useState('userType');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const cooldownRef = useRef(null);

  const handleSignUp = async (e) => {
    e.preventDefault();
    // clear previous errors;
    setSuccess(false);

    // Validation
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (!acceptTerms) {
      toast.error("You must accept the terms and conditions");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmation`,
        },
      });

      if (error) throw error;

      console.log('SignUp response:', data);

      if (data.user) {
        // Check if email confirmation is required
        if (data.user.identities && data.user.identities.length === 0) {
          // User already exists
          toast.error('User already registered. Please sign in.');
        } else if (data.user.confirmed_at) {
          // Email already confirmed (auto-confirm is enabled)
          setSuccess(true);
          setTimeout(() => navigate(`/onboarding?d=${encodeURIComponent(encodeData({ firstName, lastName, userTypes }))}`), 2000);
        } else {
          // Email confirmation required
          setSuccess(true);
          startCooldown(60);
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // clear previous errors;
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/register-callback?d=${encodeURIComponent(encodeData({ userTypes }))}`,
        },
      });

      if (error) throw error;
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };

  const handleMicrosoftSignUp = async () => {
    setLoading(true);
    // clear previous errors;

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location.origin}/register-callback?d=${encodeURIComponent(encodeData({ userTypes }))}`,
          scopes: 'email'
        },
      });

      console.log('OAuth response:', { data, error });

      if (error) throw error;
    } catch (err) {
      console.error('Microsoft OAuth error:', err);
      toast.error(err.message);
      setLoading(false);
    }
  };

  const startCooldown = (seconds) => {
    setResendCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => clearInterval(cooldownRef.current);
  }, []);

  const handleResendVerification = async () => {
    setLoading(true);
    // clear previous errors;
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmation`,
        },
      });
      if (error) throw error;
      startCooldown(60);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNextAfterVerification = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        toast.warning('Please confirm your email address before continuing.');
        return;
      }
      navigate(`/onboarding?d=${encodeURIComponent(encodeData({ firstName, lastName, userTypes }))}`);

    } catch {
      toast.warning('Please confirm your email address before continuing.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (userTypes.length > 0) {
      setCurrentStep('form');
    }
  };

  return (
     <>
          {/* Header */}
          <div className="text-center lg:text-left mb-6">
            <h2 className="text-3xl font-bold text-primary-500">{success ? "Verify Email" : "Create an account"}</h2>
          </div>

          {/* Verify Email Screen */}
          {success && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We've sent an email to <span className="font-bold text-gray-900">{email}</span>
              </p>
              <p className="text-sm text-gray-600">
                Recovery link will expire in 2 minutes.
              </p>
              <p className="text-sm text-gray-600">
                Open the email and click on the link provided to verify your email address - come back here and tap next below when you're done.
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={loading || resendCooldown > 0}
                className="text-sm text-primary-500 hover:underline font-medium disabled:opacity-50 disabled:no-underline"
              >
                {resendCooldown > 0
                  ? `Resend Verification Email (${resendCooldown}s)`
                  : 'Resend Verification Email'}
              </button>
            </div>
          )}


          {/* Show form only if not successful */}
          {!success && (
            <div className="flex-1 relative overflow-hidden">
              <div 
                className={`transition-transform duration-500 ease-in-out ${
                  currentStep === 'userType' ? 'translate-x-0' : '-translate-x-full'
                }`}
              >
                {/* User Type Selection - Show only on first step */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">I am a...</Label>
                  <div className="space-y-2">
                    {[
                      { value: "broker", label: "Broker" },
                      { value: "operator", label: "Operator" },
                      //{ value: "client", label: "Client" }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-700">{option.label}</span>
                        <input
                          type="checkbox"
                          value={option.value}
                          checked={userTypes.includes(option.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserTypes([...userTypes, option.value]);
                            } else {
                              setUserTypes(userTypes.filter(type => type !== option.value));
                            }
                          }}
                          className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500 rounded"
                          disabled={loading}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div 
                className={`absolute top-0 left-0 w-full h-full overflow-y-auto transition-transform duration-500 ease-in-out ${
                  currentStep === 'form' ? 'translate-x-0' : 'translate-x-full'
                }`}
              >
                {/* Sign Up Form - Show only on second step */}
                <div className="space-y-4">
                  <form onSubmit={handleSignUp} className="space-y-4">

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name <span className="text-[#0C3E71]"> *</span></Label>
                        <Input
                          id="firstName"
                          type="text"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          disabled={loading}
                          className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name <span className="text-[#0C3E71]"> *</span></Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                          disabled={loading}
                          className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email <span className="text-[#0C3E71]"> *</span></Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                      />
                    </div>

                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required={true}
                      disabled={loading}
                      label="Password"
                      showLabel={true}
                      showValidation={true}
                    />

                    <PasswordInput
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required={true}
                      disabled={loading}
                      label="Confirm Password"
                      showLabel={true}
                      showValidation={false}
                    />

                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        disabled={loading}
                        className="mt-0.5 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="terms"
                        className="text-sm text-slate-600 leading-tight cursor-pointer"
                      >
                        I accept the{" "}
                        <a href="#" className="text-[#0C3E71] hover:underline">
                          Terms and Conditions
                        </a>{" "}
                        and{" "}
                        <a href="#" className="text-[#0C3E71] hover:underline">
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </form>

                  <div className="space-y-4 mt-5">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">Or signup with</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <button
                        type="button"
                        onClick={handleGoogleSignUp}
                        disabled={loading}
                        className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-gray-300 shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 rounded-full"
                      >
                        <RiGoogleLine className="w-5 h-5 mr-2 text-red-500" />
                        Continue with Google
                      </button>

                      <button
                        type="button"
                        onClick={handleMicrosoftSignUp}
                        disabled={loading}
                        className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-gray-300 shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 rounded-full"
                      >
                        <RiMicrosoftLine className="w-5 h-5 mr-2 text-blue-500" />
                        Continue with Microsoft
                      </button>
                    </div>
                  </div>
       
                    <p className="text-center text-sm text-slate-600">
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setAuthType("signin")}
                        className="text-primary-500 hover:underline font-medium"
                      >
                        Sign in
                      </button>
                    </p>
               
                </div>
              </div>
            </div>
          )} 
          <div className="flex space-x-4 mt-auto pt-6">
            <Button
              type="button"
              variant="secondary"
              mode="stroke"
              className="flex-1 !border-border-light !font-normal rounded-full"
              disabled={loading}
              onClick={currentStep === 'form' && !success ? () => setCurrentStep('userType') : () => setAuthType("home")}
            >
              {currentStep === 'form' && !success ? "Back" : "Cancel"}
            </Button>
            <Button
              type="button"
              className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-border-light disabled:text-gray-400 rounded-full"
              disabled={loading || (currentStep === 'userType' && userTypes.length === 0)}
              onClick={
                success
                  ? handleNextAfterVerification
                  : currentStep === 'userType'
                    ? handleNext
                    : handleSignUp
              }
            >
              {success ? "Next" : currentStep === 'userType' ? "Next" : "Create account"}
            </Button>
        </div>
    </>
  );
}

// Default export for backward compatibility
export default function SignUp() {
  return <SignUpForm />;
}
